import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatDate, formatDateTime } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/app/ui-bits";
import { Plus, ListChecks, PencilSimple, Trash, CheckCircle, XCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import ExportMenu from "@/components/app/ExportMenu";
import TimesheetsPanel from "@/components/app/TimesheetsPanel";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";

import LinkedRecordPicker from "@/components/app/LinkedRecordPicker";

const MODULES = [
  { key: "project",    label: "Project" },
  { key: "asset",      label: "Asset Management" },
  { key: "finance",    label: "Finance" },
  { key: "hr",         label: "Human Resources" },
  { key: "procurement",label: "Procurement" },
  { key: "compliance", label: "Compliance" },
  { key: "other",      label: "Other" },
];

function AssigneePicker({ value, label, onChange, testid }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/tasks/assignees?q=${encodeURIComponent(q)}`);
        setResults(data.items || []);
      } catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      {value && (
        <div className="mb-1 flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-xs">
          <span className="font-semibold text-slate-900">{label}</span>
          <button type="button" onClick={() => onChange(null, "")} className="ml-auto text-rose-600 hover:underline">Change</button>
        </div>
      )}
      {!value && (
        <>
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Type name, staff no., position…"
            data-testid={testid}
          />
          {open && q.length >= 2 && (
            <div className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
              {loading && <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>}
              {!loading && results.length === 0 && (
                <div className="px-3 py-2 text-xs text-rose-600">No matching user found</div>
              )}
              {results.map((r) => (
                <button
                  key={r.email} type="button"
                  onClick={() => { onChange(r, r.name); setQ(""); setOpen(false); }}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                  data-testid={`assignee-opt-${r.email}`}
                >
                  <div className="font-semibold text-slate-900">{r.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {[r.staff_no, r.position, r.department, r.role].filter(Boolean).join(" · ")}
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TaskForm({ initial, projects, staff, onSave, children }) {
  const [open, setOpen] = useState(false);
  const blankForm = () => ({
    title: "", description: "",
    related_module: "project",
    project_id: "", linked_record_id: "", linked_record_label: "",
    deliverable_id: "",
    assigned_to: "", assigned_to_email: "", assigned_to_name: "",
    reviewer_email: "", reviewer_name: "",
    approver_email: "", approver_name: "",
    priority: "medium",
    status: "pending",
    start_date: "", due_date: "",
  });
  const [form, setForm] = useState(initial || blankForm());
  const [busy, setBusy] = useState(false);
  // Tracks which fields were AUTO-filled this session so we show a clear chip.
  const [autofilled, setAutofilled] = useState({});

  useEffect(() => {
    if (open) {
      setForm(initial || blankForm());
      setAutofilled({});
    }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form };
      if (payload.related_module === "project") {
        payload.linked_record_id = payload.project_id || payload.linked_record_id;
      }
      await onSave(payload); setOpen(false);
    }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const project = projects.find((p) => p.id === form.project_id);
  const deliverables = project?.deliverables || [];

  // Auto-fill helpers: invoked when the user picks a deliverable or a
  // linked record from a non-project module. They populate title /
  // due_date / description / suggested-assignee so the user types less.
  const applyDeliverable = (deliv) => {
    if (!deliv) {
      setForm((f) => ({ ...f, deliverable_id: "" }));
      return;
    }
    const next = {
      ...form,
      deliverable_id: deliv.id,
      // Auto-fill title from deliverable if user hasn't typed one yet
      title: form.title?.trim() ? form.title : (deliv.title || ""),
      description: form.description?.trim() ? form.description : (deliv.description || ""),
      due_date: form.due_date || deliv.due_date || "",
      priority: form.priority === "medium" && deliv.priority ? deliv.priority : form.priority,
    };
    // Auto-fill assignee from deliverable.owner if the user hasn't selected one
    if (!form.assigned_to && deliv.owner) {
      next.assigned_to = deliv.owner;
      next.assigned_to_email = deliv.owner;
      next.assigned_to_name = deliv.owner;
    }
    setForm(next);
    setAutofilled({
      title: !form.title?.trim() && deliv.title ? true : autofilled.title,
      description: !form.description?.trim() && deliv.description ? true : autofilled.description,
      due_date: !form.due_date && deliv.due_date ? true : autofilled.due_date,
      assigned_to: !form.assigned_to && deliv.owner ? true : autofilled.assigned_to,
    });
  };

  const applyLinkedRecord = (rec) => {
    if (!rec) {
      setForm((f) => ({ ...f, linked_record_id: "", linked_record_label: "" }));
      setAutofilled({});
      return;
    }
    const next = {
      ...form,
      linked_record_id: rec.id || rec._id,
      linked_record_label: rec._display_title || rec.name || rec.title || "",
      title: form.title?.trim() ? form.title : (rec._display_title || rec.name || rec.title || ""),
      description: form.description?.trim() ? form.description
                    : (rec.description || rec.notes || rec.summary || ""),
      due_date: form.due_date || rec.due_date || rec.expiry_date || rec.calibration_due || "",
    };
    setForm(next);
    setAutofilled({
      title: !form.title?.trim() ? true : autofilled.title,
      description: !form.description?.trim() && (rec.description || rec.notes || rec.summary) ? true : autofilled.description,
      due_date: !form.due_date && (rec.due_date || rec.expiry_date || rec.calibration_due) ? true : autofilled.due_date,
    });
  };

  // Visible "linked context" summary so the user can see WHAT this task is
  // tied to before saving.
  const renderContext = () => {
    const chips = [];
    if (project) chips.push({ k: "Project", v: project.name + (project.project_code ? ` (${project.project_code})` : "") });
    if (form.deliverable_id) {
      const d = deliverables.find((x) => x.id === form.deliverable_id);
      if (d) chips.push({ k: "Deliverable", v: d.title });
    }
    if (form.linked_record_label && form.related_module !== "project") {
      chips.push({ k: MODULES.find((m) => m.key === form.related_module)?.label || "Record", v: form.linked_record_label });
    }
    if (chips.length === 0) return null;
    return (
      <div className="col-span-2 rounded-md border border-blue-200 bg-blue-50/40 px-3 py-2" data-testid="task-context-summary">
        <div className="font-mono-alt text-[10px] uppercase tracking-wider text-blue-700 mb-1">Linked context</div>
        <div className="flex flex-wrap gap-2">
          {chips.map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded bg-white border border-blue-200 px-1.5 py-0.5 text-[11px] text-slate-700">
              <span className="font-mono-alt text-[9px] uppercase text-blue-600">{c.k}</span>
              <span className="truncate max-w-[280px]">{c.v}</span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{initial?.id ? "Edit task" : "Task breakdown"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          {/* Linked context summary (auto-shown when project/deliverable/linked record is picked) */}
          {renderContext()}

          <div>
            <Label>Related module *</Label>
            <Select value={form.related_module} onValueChange={(v) => setForm({ ...form, related_module: v, project_id: "", linked_record_id: "", linked_record_label: "", deliverable_id: "" })}>
              <SelectTrigger data-testid="task-module-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODULES.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.related_module === "project" ? (
            <>
              <div>
                <Label>Project</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v, deliverable_id: "" })}>
                  <SelectTrigger data-testid="task-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.project_code ? ` · ${p.project_code}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Deliverable
                  {form.deliverable_id && (autofilled.title || autofilled.due_date || autofilled.assigned_to) && (
                    <span className="ml-1.5 font-mono-alt text-[9px] uppercase rounded bg-emerald-100 text-emerald-800 px-1 py-0.5" title="Title / due / assignee auto-filled from this deliverable">auto-filled</span>
                  )}
                </Label>
                <Select value={form.deliverable_id || ""} onValueChange={(v) => {
                  const d = deliverables.find((x) => x.id === v);
                  applyDeliverable(d);
                }} disabled={!form.project_id}>
                  <SelectTrigger data-testid="task-deliverable-select"><SelectValue placeholder="Pick to auto-fill" /></SelectTrigger>
                  <SelectContent>
                    {deliverables.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}{d.due_date ? ` · due ${d.due_date}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : form.related_module !== "other" ? (
            <div className="col-span-2">
              <Label>Linked record (live lookup)
                {(autofilled.title || autofilled.due_date) && (
                  <span className="ml-1.5 font-mono-alt text-[9px] uppercase rounded bg-emerald-100 text-emerald-800 px-1 py-0.5">auto-filled</span>
                )}
              </Label>
              <LinkedRecordPicker
                module={form.related_module}
                value={form.linked_record_id}
                label={form.linked_record_label}
                onPick={applyLinkedRecord}
              />
            </div>
          ) : (
            <div className="col-span-2">
              <Label>Reference label</Label>
              <Input
                value={form.linked_record_label || ""}
                onChange={(e) => setForm({ ...form, linked_record_label: e.target.value })}
                placeholder="Free-text reference for ad-hoc tasks"
                data-testid="task-other-ref"
              />
            </div>
          )}

          <div className="col-span-2">
            <Label>Title *
              {autofilled.title && <span className="ml-1.5 font-mono-alt text-[9px] uppercase rounded bg-emerald-100 text-emerald-800 px-1 py-0.5">auto-filled · editable</span>}
            </Label>
            <Input required value={form.title} onChange={(e) => { setForm({ ...form, title: e.target.value }); setAutofilled({ ...autofilled, title: false }); }} data-testid="task-title-input" />
          </div>
          <div className="col-span-2">
            <Label>Description
              {autofilled.description && <span className="ml-1.5 font-mono-alt text-[9px] uppercase rounded bg-emerald-100 text-emerald-800 px-1 py-0.5">auto-filled · editable</span>}
            </Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => { setForm({ ...form, description: e.target.value }); setAutofilled({ ...autofilled, description: false }); }} data-testid="task-description-input" />
          </div>

          <div className="col-span-2">
            <Label>Assignee · who does the work *
              {autofilled.assigned_to && <span className="ml-1.5 font-mono-alt text-[9px] uppercase rounded bg-emerald-100 text-emerald-800 px-1 py-0.5">suggested from deliverable</span>}
            </Label>
            <AssigneePicker
              value={form.assigned_to}
              label={form.assigned_to_name || form.assigned_to_email}
              testid="task-assignee-input"
              onChange={(r) => {
                if (!r) return setForm({ ...form, assigned_to: "", assigned_to_email: "", assigned_to_name: "" });
                setForm({ ...form, assigned_to: r.id, assigned_to_email: r.email, assigned_to_name: r.name });
                setAutofilled({ ...autofilled, assigned_to: false });
              }}
            />
            <div className="text-[11px] text-slate-500 mt-1">Only existing users / HR staff can be selected — no ghost assignees.</div>
          </div>

          <div className="col-span-2">
            <Label>Reviewer · accepts / rejects / returns submitted work
              <span className="ml-1.5 font-mono-alt text-[9px] uppercase rounded bg-slate-100 text-slate-600 px-1 py-0.5">manager+ recommended</span>
            </Label>
            <AssigneePicker
              value={form.reviewer_email}
              label={form.reviewer_name || form.reviewer_email}
              testid="task-reviewer-input"
              onChange={(r) => {
                if (!r) return setForm({ ...form, reviewer_email: "", reviewer_name: "" });
                setForm({ ...form, reviewer_email: r.email, reviewer_name: r.name });
              }}
            />
          </div>

          <div className="col-span-2">
            <Label>Approver · final sign-off (optional)
              <span className="ml-1.5 font-mono-alt text-[9px] uppercase rounded bg-slate-100 text-slate-600 px-1 py-0.5">CEO / management for sensitive tasks</span>
            </Label>
            <AssigneePicker
              value={form.approver_email}
              label={form.approver_name || form.approver_email}
              testid="task-approver-input"
              onChange={(r) => {
                if (!r) return setForm({ ...form, approver_email: "", approver_name: "" });
                setForm({ ...form, approver_email: r.email, approver_name: r.name });
              }}
            />
          </div>

          <div>
            <Label>Status</Label>
            <Select value={form.status || "pending"} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="submitted">Submitted for review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Start date</Label>
            <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="task-start-date" />
          </div>
          <div className="col-span-2">
            <Label>Due date
              {autofilled.due_date && <span className="ml-1.5 font-mono-alt text-[9px] uppercase rounded bg-emerald-100 text-emerald-800 px-1 py-0.5">auto-filled · editable</span>}
            </Label>
            <Input type="date" value={form.due_date || ""} onChange={(e) => { setForm({ ...form, due_date: e.target.value }); setAutofilled({ ...autofilled, due_date: false }); }} data-testid="task-due-date" />
          </div>
          <DialogFooter className="col-span-2">
            <Button type="submit" disabled={busy || !form.assigned_to || !form.title?.trim()} data-testid="task-save-button">{busy ? "Saving…" : "Save task"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SubmitDialog({ taskId, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ submission_notes: "", submission_link: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/tasks/${taskId}/submit`, form); toast.success("Work submitted for review"); setOpen(false); onDone?.(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="rounded-sm" data-testid={`submit-${taskId}`}><PaperPlaneTilt size={12} className="mr-1" /> Submit</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Submit work for review</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Notes *</Label><Textarea required rows={4} value={form.submission_notes} onChange={(e) => setForm({ ...form, submission_notes: e.target.value })} data-testid="submission-notes-input" /></div>
          <div><Label>Link (optional)</Label><Input value={form.submission_link} onChange={(e) => setForm({ ...form, submission_link: e.target.value })} placeholder="https://…" /></div>
          <DialogFooter><Button type="submit">Submit</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({ taskId, decision, label, Icon, onDone, variant }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/tasks/${taskId}/review`, { decision, review_notes: notes }); toast.success(`Task ${decision}`); setOpen(false); onDone?.(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant={variant || "outline"} className="rounded-sm" data-testid={`review-${decision}-${taskId}`}>
          <Icon size={12} className="mr-1" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{label} task</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Review notes</Label><Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="review-notes-input" /></div>
          <DialogFooter><Button type="submit">{label}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const PRIORITY_COLORS = { low: "text-zinc-500", medium: "text-blue-700", high: "text-amber-700", critical: "text-red-700" };

export default function Tasks() {
  const { user } = useAuth();
  const inspector = useRecordInspector();
  // Compliance Officer gets a personal "my-assigned-tasks" view only.
  // They cannot assign / edit / delete / review tasks, nor see the timesheet
  // module or other people's work. Backend RBAC still enforces this at the
  // API level — these flags only collapse the UI.
  const isComplianceOnly = user?.role === "compliance_officer";
  const canAssign = (can(user, "tasks.create") ?? hasMinRole(user, "manager")) && !isComplianceOnly;
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [section, setSection] = useState("board"); // board | timesheets

  const load = async () => {
    const [t, p, s] = await Promise.all([api.get("/tasks"), api.get("/projects"), api.get("/hr/staff")]);
    setTasks(t.data); setProjects(p.data); setStaff(s.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Force board section for compliance — they don't get timesheets either.
  useEffect(() => {
    if (isComplianceOnly && section !== "board") setSection("board");
  }, [isComplianceOnly, section]);

  const save = async (form) => {
    if (form.id) { await api.patch(`/tasks/${form.id}`, form); toast.success("Task updated"); }
    else { await api.post("/tasks", form); toast.success("Task assigned"); }
    load();
  };

  const remove = async (id) => {
    try { await api.delete(`/tasks/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";
  // Resolve assignee display name with fallbacks:
  //   1. The denormalized assigned_to_name on the task itself (set at create time)
  //   2. HR staff lookup by id
  //   3. The assigned_to_email
  //   4. A short version of the id as last resort
  const assigneeMeta = (t) => {
    if (t.assigned_to_name) {
      const s = staff.find((x) => x.id === t.assigned_to || x.email === t.assigned_to_email);
      return {
        name: t.assigned_to_name,
        sub: s ? [s.designation, s.department].filter(Boolean).join(" · ") : (t.assigned_to_email || ""),
      };
    }
    const s = staff.find((x) => x.id === t.assigned_to || x.email === t.assigned_to_email);
    if (s) return { name: s.full_name, sub: [s.designation, s.department].filter(Boolean).join(" · ") };
    if (t.assigned_to_email) return { name: t.assigned_to_email, sub: "" };
    return { name: (t.assigned_to || "").slice(0, 8) + "…", sub: "" };
  };

  const filtered = useMemo(() => {
    let base = tasks;
    // Compliance Officer: hard scope to tasks assigned to them only.
    if (isComplianceOnly) {
      base = base.filter((t) =>
        t.assigned_to === user?.id ||
        t.assigned_to_email === user?.email
      );
    }
    if (filter === "all") return base;
    if (filter === "pending_review") return base.filter((t) => t.status === "submitted");
    return base.filter((t) => t.status === filter);
  }, [tasks, filter, isComplianceOnly, user]);

  return (
    <div data-testid="tasks-page">
      <PageHeader
        eyebrow={isComplianceOnly ? "My Workspace" : "Workflow"}
        title={isComplianceOnly ? "My Assigned Tasks" : "Task Breakdown"}
        description={isComplianceOnly
          ? "Tasks specifically assigned to you. Submit your work for review when complete."
          : "Assign tasks against project deliverables. Review and approve or reject submissions."}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {section === "board" && (
              <>
                <ExportMenu module="tasks" filters={{ status: filter !== "all" ? filter : undefined }} />
                {canAssign && (
                  <TaskForm projects={projects} staff={staff} onSave={save}>
                    <Button className="rounded-sm" data-testid="add-task-button"><Plus size={14} className="mr-2" /> Assign task</Button>
                  </TaskForm>
                )}
              </>
            )}
          </div>
        }
      />
      <div className="p-8 space-y-4">
        {/* Section switcher — hidden for compliance-only role */}
        {!isComplianceOnly && (
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="tasks-section-switcher">
          <button
            type="button"
            onClick={() => setSection("board")}
            data-testid="tasks-section-board"
            className={`px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition ${
              section === "board" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            Task Board
          </button>
          <button
            type="button"
            onClick={() => setSection("timesheets")}
            data-testid="tasks-section-timesheets"
            className={`px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm transition ${
              section === "timesheets" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            Timesheets / Effort
          </button>
        </div>
        )}

        {section === "timesheets" ? (
          <TimesheetsPanel />
        ) : (
          <>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="rounded-sm">
            <TabsTrigger value="all" className="rounded-sm">All</TabsTrigger>
            <TabsTrigger value="assigned" className="rounded-sm">Assigned</TabsTrigger>
            <TabsTrigger value="submitted" className="rounded-sm">Pending review</TabsTrigger>
            <TabsTrigger value="approved" className="rounded-sm">Approved</TabsTrigger>
            <TabsTrigger value="rejected" className="rounded-sm">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="font-mono-alt text-xs uppercase tracking-[0.25em] text-zinc-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={ListChecks} title="No tasks" description="Assign tasks to staff to start tracking deliverable-level work." />
        ) : (
          <div className="space-y-3">
            {filtered.map((t) => {
              const a = assigneeMeta(t);
              const overdue = t.due_date && t.status !== "approved" && t.status !== "completed" && new Date(t.due_date) < new Date();
              const progress = Math.max(0, Math.min(100, t.progress_pct ?? (t.status === "approved" || t.status === "completed" ? 100 : t.status === "submitted" ? 80 : t.status === "in_progress" ? 50 : t.status === "assigned" ? 10 : 0)));
              const PRIORITY_TONE = { low: "bg-zinc-100 text-zinc-700 border-zinc-200", medium: "bg-blue-50 text-blue-700 border-blue-200", high: "bg-amber-50 text-amber-700 border-amber-200", critical: "bg-red-50 text-red-700 border-red-200" };
              return (
              <div key={t.id} className="dc-card overflow-hidden" data-testid={`task-row-${t.id}`}>
                <div
                  className="p-4 md:p-5 cursor-pointer hover:bg-slate-50/40 transition-colors"
                  onClick={() => inspector.open(t, {
                    title: t.title || "Task",
                    subtitle: `${(projectName(t.project_id) !== "—" ? projectName(t.project_id) : t.linked_record_label || t.related_module || "")} · ${a.name}`,
                  })}
                  role="button"
                  tabIndex={0}
                  data-testid={`task-card-${t.id}`}
                >
                  <div className="flex flex-wrap items-start gap-2 mb-2">
                    <span className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider ${PRIORITY_TONE[t.priority] || "border-zinc-200 text-zinc-700"}`}>
                      {t.priority}
                    </span>
                    <StatusBadge status={t.status} />
                    {overdue && (
                      <span className="inline-flex items-center rounded border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider text-red-700" data-testid={`task-overdue-${t.id}`}>
                        Overdue
                      </span>
                    )}
                    {t.related_module && t.related_module !== "project" && (
                      <span className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider text-slate-700">
                        {t.related_module}
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-base font-semibold text-zinc-950 break-words">{t.title}</h3>
                  {t.description && <p className="mt-1 text-sm text-zinc-600 break-words">{t.description}</p>}

                  <div className="mt-3 grid grid-cols-1 gap-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <span className="overline block text-slate-500">Project / Link</span>
                      <span className="text-zinc-900 font-medium break-words">{projectName(t.project_id) || t.linked_record_label || "—"}</span>
                    </div>
                    <div data-testid={`task-assignee-${t.id}`}>
                      <span className="overline block text-slate-500">Assignee</span>
                      <span className="text-zinc-900 font-medium block truncate">{a.name}</span>
                      {a.sub && <span className="text-[10px] text-slate-500 block truncate">{a.sub}</span>}
                    </div>
                    <div>
                      <span className="overline block text-slate-500">Due</span>
                      <span className={`font-mono-alt block ${overdue ? "text-red-700 font-semibold" : "text-zinc-900"}`}>{formatDate(t.due_date) || "—"}</span>
                    </div>
                    <div>
                      <span className="overline block text-slate-500">Created</span>
                      <span className="font-mono-alt text-zinc-900 block">{formatDate(t.created_at) || "—"}</span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-3" data-testid={`task-progress-${t.id}`}>
                    <div className="flex items-baseline justify-between text-[10px] mb-1">
                      <span className="overline text-slate-500">Progress</span>
                      <span className="font-mono-alt tabular-nums font-semibold text-slate-700">{progress}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full transition-all duration-500 ${progress >= 100 ? "bg-emerald-500" : progress >= 50 ? "bg-blue-500" : "bg-amber-500"}`} style={{ width: `${progress}%` }} />
                    </div>
                  </div>

                  {t.submission && (
                    <div className="mt-4 border-l-2 border-blue-600 bg-blue-50 p-3 text-xs">
                      <div className="font-mono-alt uppercase tracking-wider text-blue-700 break-words">
                        Submitted by {t.submission.submitted_by_email} · {formatDateTime(t.submission.submitted_at)}
                      </div>
                      <div className="mt-1 text-zinc-800 break-words">{t.submission.notes}</div>
                      {t.submission.link && <a className="mt-1 block underline break-all" href={t.submission.link} target="_blank" rel="noreferrer">{t.submission.link}</a>}
                    </div>
                  )}
                  {t.review && (
                    <div className={`mt-3 border-l-2 p-3 text-xs ${t.review.decision === "approved" ? "border-emerald-600 bg-emerald-50" : "border-red-600 bg-red-50"}`}>
                      <div className="font-mono-alt uppercase tracking-wider break-words">{t.review.decision} · by {t.review.reviewed_by_email} · {formatDateTime(t.review.reviewed_at)}</div>
                      {t.review.notes && <div className="mt-1 text-zinc-800 break-words">{t.review.notes}</div>}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-zinc-200 bg-zinc-50 px-4 py-2 md:px-5" onClick={(e) => e.stopPropagation()}>
                  <AuditTrailDrawer entity="task" entityId={t.id} label={t.title} />
                  {t.status === "assigned" && (t.assigned_to === user.id || t.assigned_to_email === user.email || canAssign) && (
                    <SubmitDialog taskId={t.id} onDone={load} />
                  )}
                  {t.status === "submitted" && canAssign && (
                    <>
                      <ReviewDialog taskId={t.id} decision="approved" label="Approve" Icon={CheckCircle} onDone={load} />
                      <ReviewDialog taskId={t.id} decision="rejected" label="Reject" Icon={XCircle} onDone={load} variant="outline" />
                    </>
                  )}
                  {t.status === "rejected" && (t.assigned_to === user.id || t.assigned_to_email === user.email || canAssign) && (
                    <SubmitDialog taskId={t.id} onDone={load} />
                  )}
                  {canAssign && (
                    <>
                      <TaskForm initial={t} projects={projects} staff={staff} onSave={(f) => save({ ...f, id: t.id })}>
                        <Button size="sm" variant="outline" className="rounded-sm"><PencilSimple size={12} /></Button>
                      </TaskForm>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="sm" variant="outline" className="rounded-sm"><Trash size={12} /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete task?</AlertDialogTitle></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(t.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
      <RecordInspector {...inspector.props} />
    </div>
  );
}
